import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "../lib/api";
import { errMsg } from "../lib/format";
import { useAuth } from "../context/AuthContext";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Lock, Mail, ShieldCheck } from "lucide-react";

export default function Login() {
  const { login, user, checked } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    document.documentElement.classList.remove("dark");
  }, []);

  useEffect(() => {
    if (checked && user) navigate(user.role === "admin" ? "/admin" : "/", { replace: true });
  }, [checked, user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      login(data.token, data.user);
      navigate(data.user.role === "admin" ? "/admin" : "/", { replace: true });
    } catch (err) {
      setError(errMsg(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      {/* left visual */}
      <div className="hidden lg:block relative bg-[#09090B]">
        <img
          src="https://images.unsplash.com/photo-1488972685288-c3fd157d7c7a"
          alt="architecture"
          className="absolute inset-0 w-full h-full object-cover opacity-40"
        />
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(9,9,11,0.4) 0%, rgba(9,9,11,0.95) 100%)" }} />
        <div className="relative z-10 h-full flex flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-2">
            <ShieldCheck className="text-[#059669]" size={24} />
            <span className="font-heading font-extrabold text-2xl tracking-tight">Swiftors</span>
          </div>
          <div>
            <h2 className="font-heading font-extrabold text-4xl leading-tight tracking-tight">
              Precise payments.<br />Ledger-grade trust.
            </h2>
            <p className="mt-4 text-white/50 max-w-md text-sm">
              A secure deposit &amp; payout panel with per-user UPI/QR, manual UTR
              settlement and a strict balance ledger.
            </p>
          </div>
          <p className="overline text-white/30">Enterprise Payment Infrastructure</p>
        </div>
      </div>

      {/* right form */}
      <div className="flex items-center justify-center px-6 py-12">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="w-full max-w-sm"
        >
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <ShieldCheck className="text-[#059669]" size={22} />
            <span className="font-heading font-extrabold text-2xl tracking-tight">Swiftors</span>
          </div>
          <p className="overline">Welcome back</p>
          <h1 className="font-heading font-extrabold text-3xl tracking-tight mt-1 mb-8">Sign in to your account</h1>

          <form onSubmit={submit} className="space-y-5">
            <div>
              <Label className="overline">Email</Label>
              <div className="relative mt-2">
                <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  data-testid="login-email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="pl-9 rounded-sm h-11"
                />
              </div>
            </div>
            <div>
              <Label className="overline">Password</Label>
              <div className="relative mt-2">
                <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  data-testid="login-password"
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="pl-9 rounded-sm h-11"
                />
              </div>
            </div>

            {error && (
              <div data-testid="login-error" className="text-sm text-[#DC2626] bg-[#DC2626]/10 px-3 py-2 rounded-sm">
                {error}
              </div>
            )}

            <Button
              data-testid="login-submit"
              type="submit"
              disabled={loading}
              className="w-full h-11 rounded-sm active:scale-[0.98] transition-transform"
            >
              {loading ? "Signing in…" : "Sign In"}
            </Button>
          </form>
          <p className="text-xs text-muted-foreground mt-6">
            Accounts are provisioned by the administrator. Contact your admin for access.
          </p>
        </motion.div>
      </div>
    </div>
  );
}
